import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { defaultFilters, fetchDashboard } from "./dashboardApi";
import {
    ZMLayout,
    ZMCard,
    ZMMetricCard,
    ZMTable,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
    ZMBadge,
} from "./components/ZMUI";
import { Icono } from "./iconos";

const fmt = (n, dec = 0) =>
    n == null ? "0" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const inconsistencyTone = (tipo) => {
    const key = String(tipo || "").toLowerCase();
    if (key.includes("duplic")) return "contactado";
    if (key.includes("faltan") || key.includes("sin_")) return "rechazado";
    return "calificado";
};

export default function ZMAuditoria() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState(() => defaultFilters());
    const [draftFilters, setDraftFilters] = useState(() => defaultFilters());
    const [datos, setDatos] = useState(null);

    const cargar = async (nextFilters = filters) => {
        const token = getToken();
        if (!token) {
            navigate("/");
            return;
        }

        setCargando(true);
        setError("");
        try {
            const payload = await fetchDashboard("auditoria-datos", token, nextFilters);
            setDatos(payload);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") {
                navigate("/");
                return;
            }
            setError("No se pudo cargar la auditoría de datos.");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargar(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const totales = datos?.totales || {};
    const porFuente = datos?.porFuente || [];
    const porCampania = datos?.porCampania || [];
    const porFormulario = datos?.porFormulario || [];
    const duplicados = datos?.duplicados || [];
    const inconsistencias = datos?.inconsistencias || [];
    const recomendaciones = datos?.recomendacionesTecnicas || [];

    const leadsSinAtribucion = [
        { tipo: "Sin campaña", total: totales.leadsSinCampania || 0 },
        { tipo: "Sin formulario", total: totales.leadsSinFormulario || 0 },
    ];

    if (cargando) return <ZMLoadingState label="Cargando auditoría de datos..." fullScreen />;
    if (error) return <ZMErrorState message={error} onRetry={() => cargar(filters)} />;
    if (!datos) return <ZMEmptyState title="Sin auditoría disponible" hint="No se pudo construir el análisis para el período elegido." />;

    return (
        <ZMLayout
            title="Auditoría de datos"
            subtitle="Comparación entre Leads CRM y leads reportados por Meta para detectar diferencias de atribución."
            right={datos?.meta?.disponible ? "Meta + CRM" : "Solo CRM"}
            className={darkMode ? "container-dark" : ""}
        >
            <ZMFiltersBar className="mb-3">
                <div>
                    <label>Desde</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaDesde || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaDesde: e.target.value }))} />
                </div>
                <div>
                    <label>Hasta</label>
                    <input type="date" className="form-control form-control-sm" value={draftFilters.fechaHasta || ""} onChange={(e) => setDraftFilters((p) => ({ ...p, fechaHasta: e.target.value }))} />
                </div>
                <div style={{ minWidth: 240 }}>
                    <label>Campaña</label>
                    <input className="form-control form-control-sm" value={draftFilters.campania || ""} placeholder="ID de campaña o vacío" onChange={(e) => setDraftFilters((p) => ({ ...p, campania: e.target.value }))} />
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={() => {
                        setFilters({ ...draftFilters });
                        cargar({ ...draftFilters });
                    }}>
                        Aplicar
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                        const reset = defaultFilters();
                        setDraftFilters(reset);
                        setFilters(reset);
                        cargar(reset);
                    }}>
                        Limpiar
                    </button>
                </div>
            </ZMFiltersBar>

            <div className="zm-grid-metrics">
                <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmt(totales.leadsCrm, 0)} helper="Fuente principal del panel" />
                <ZMMetricCard icon={<Icono.BarChart />} label="Leads reportados por Meta" value={fmt(totales.leadsMetaReported, 0)} helper="Actions/insights de Meta" />
                <ZMMetricCard icon={<Icono.CheckCircle />} label="Leads únicos" value={fmt(totales.leadsUnicos, 0)} helper="Base deduplicada" />
                <ZMMetricCard icon={<Icono.AlertTriangle />} label="Duplicados" value={fmt(totales.leadsDuplicados, 0)} helper="Detectados por leadgen_id/teléfono/email" />
                <ZMMetricCard icon={<Icono.Target />} label="Sin campaña" value={fmt(totales.leadsSinCampania, 0)} />
                <ZMMetricCard icon={<Icono.Eye />} label="Sin formulario" value={fmt(totales.leadsSinFormulario, 0)} />
                <ZMMetricCard icon={<Icono.Wallet />} label="Campañas con leads" value={fmt(totales.campaniasConLeads, 0)} />
                <ZMMetricCard icon={<Icono.Star />} label="Formularios con leads" value={fmt(totales.formulariosConLeads, 0)} />
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-xl-8">
                    <ZMCard title="Diferencias por campaña">
                        <ZMTable
                            rows={porCampania}
                            emptyText="Sin campañas para auditar."
                            columns={[
                                { key: "campaignName", header: "Campaña", render: (row) => row.campaignName || row.campaignId || "Sin campaña" },
                                { key: "leadsCrm", header: "Leads CRM", cellClassName: "text-end", render: (row) => fmt(row.leadsCrm, 0) },
                                { key: "leadsMetaReported", header: "Meta reportados", cellClassName: "text-end", render: (row) => fmt(row.leadsMetaReported, 0) },
                                { key: "leadsDiferencia", header: "Diferencia", cellClassName: "text-end", render: (row) => fmt(row.leadsDiferencia, 0) },
                                { key: "cerrados", header: "Cerrados", cellClassName: "text-end", render: (row) => fmt(row.cerrados, 0) },
                            ]}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-xl-4">
                    <ZMCard title="Leads sin atribución">
                        <ZMTable
                            rows={leadsSinAtribucion}
                            emptyText="Sin observaciones."
                            columns={[
                                { key: "tipo", header: "Caso" },
                                { key: "total", header: "Total", cellClassName: "text-end", render: (row) => fmt(row.total, 0) },
                            ]}
                        />
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                    <ZMCard title="Formularios con diferencias">
                        <ZMTable
                            rows={porFormulario}
                            emptyText="Sin formularios para mostrar."
                            columns={[
                                { key: "formName", header: "Formulario", render: (row) => row.formName || row.formId || "Sin formulario" },
                                { key: "leadsCrm", header: "Leads CRM", cellClassName: "text-end", render: (row) => fmt(row.leadsCrm, 0) },
                                { key: "leadsUnicos", header: "Únicos", cellClassName: "text-end", render: (row) => fmt(row.leadsUnicos, 0) },
                            ]}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-lg-6">
                    <ZMCard title="Fuentes de lead">
                        <ZMTable
                            rows={porFuente}
                            emptyText="Sin fuentes registradas."
                            columns={[
                                { key: "fuenteLead", header: "Fuente", render: (row) => row.fuenteLead || "MetaLeadAds" },
                                { key: "leadsCrm", header: "Leads CRM", cellClassName: "text-end", render: (row) => fmt(row.leadsCrm, 0) },
                                { key: "leadsUnicos", header: "Únicos", cellClassName: "text-end", render: (row) => fmt(row.leadsUnicos, 0) },
                                { key: "leadsDuplicados", header: "Duplicados", cellClassName: "text-end", render: (row) => fmt(row.leadsDuplicados, 0) },
                            ]}
                        />
                    </ZMCard>
                </div>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-6">
                    <ZMCard title="Duplicados detectados">
                        <ZMTable
                            rows={duplicados}
                            emptyText="No se detectaron duplicados para el filtro actual."
                            columns={[
                                { key: "reason", header: "Motivo", render: (row) => row.reason || "contacto" },
                                { key: "count", header: "Leads", cellClassName: "text-end", render: (row) => fmt(row.count || row.leads?.length, 0) },
                                { key: "primaryLeadId", header: "Lead principal", cellClassName: "text-end", render: (row) => fmt(row.primaryLeadId, 0) },
                            ]}
                        />
                    </ZMCard>
                </div>
                <div className="col-12 col-lg-6">
                    <ZMCard title="Inconsistencias detectadas">
                        {inconsistencias.length === 0 ? (
                            <ZMEmptyState title="Sin inconsistencias críticas" hint="CRM y Meta quedaron alineados para el filtro actual." />
                        ) : (
                            <div className="zm-alert-stack">
                                {inconsistencias.map((item, index) => (
                                    <div key={`${item.tipo}-${index}`} className="zm-alert-item">
                                        <div className="d-flex justify-content-between gap-2 align-items-start">
                                            <div>
                                                <strong>{item.titulo || item.tipo || "Inconsistencia"}</strong>
                                                <p>{item.mensaje || item.descripcion}</p>
                                            </div>
                                            <ZMBadge status={inconsistencyTone(item.tipo)} label={item.tipo || "revisar"} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ZMCard>
                </div>
            </div>

            <ZMCard title="Alertas y recomendaciones técnicas">
                {recomendaciones.length === 0 ? (
                    <ZMEmptyState title="Sin recomendaciones" hint="No se generaron acciones técnicas para este rango." />
                ) : (
                    <div className="zm-alert-stack">
                        {recomendaciones.map((item, index) => (
                            <div key={`${item}-${index}`} className="zm-alert-item">
                                <strong>Recomendación {index + 1}</strong>
                                <p>{item}</p>
                            </div>
                        ))}
                    </div>
                )}
            </ZMCard>
        </ZMLayout>
    );
}
